/**
 * Owner: Masa Bayu
 * File: author-publish-request.ts
 * Date:Time: 2026-09-16 14:05
 * Description: T6-E2 PublishRequest lifecycle — status timeline; ≠ QXW/QXP editor.
 */
import { assertNoForbiddenFoundationKey } from './authority';
import { authorizeAuthorSurfaceAccess } from './author-account';
import { evaluatePublishReadiness, } from './author-publish-readiness';
import { assertT6E1SecurityIntact, assertT6E2SecurityIntact } from './author-security';
import { createQxreadId } from './id';
import { assertOptionalTrimmed, assertTimestamp, assertValidId } from './guards';
export const QXREAD_PUBLISH_REQUEST_STATES = [
    'draft',
    'in_review',
    'approved',
    'live',
    'rejected',
    'withdrawn',
];
export const QXREAD_PUBLISH_PIPELINE_EVENTS = [
    'created',
    'readiness_checked',
    'submitted',
    'review_approved',
    'review_rejected',
    'released_live',
    'withdrawn',
    'self_approve_denied',
];
function assertPublishState(value, label) {
    if (typeof value !== 'string' ||
        !QXREAD_PUBLISH_REQUEST_STATES.includes(value)) {
        throw new Error(`${label} must be one of: ${QXREAD_PUBLISH_REQUEST_STATES.join(', ')}`);
    }
    return value;
}
function appendEvent(timeline, kind, at, note) {
    return Object.freeze([...timeline, Object.freeze({ kind, at, note })]);
}
/**
 * Create a draft PublishRequest under own-resource scope. Never a QXW document.
 */
export function createPublishRequest(input) {
    assertNoForbiddenFoundationKey(input, 'QXRead create publish request');
    assertT6E1SecurityIntact();
    assertT6E2SecurityIntact();
    const access = authorizeAuthorSurfaceAccess({
        session: input.session,
        account: input.account,
        authorAccount: input.authorAccount,
    });
    if (!access.allowed) {
        return Object.freeze({ allowed: false, reason: access.reason });
    }
    assertValidId(input.publicationId, 'QXRead publicationId');
    const title = assertOptionalTrimmed(input.title, 'QXRead publish title');
    if (title === null) {
        throw new Error('QXRead publish title is required');
    }
    const createdAt = input.createdAt ?? new Date().toISOString();
    assertTimestamp(createdAt, 'QXRead publish createdAt');
    const publishRequestId = input.publishRequestId !== undefined ? input.publishRequestId : createQxreadId('qxrdpubreq');
    assertValidId(publishRequestId, 'QXRead publishRequestId');
    const qxpPublicationRef = assertOptionalTrimmed(input.qxpPublicationRef, 'QXRead qxpPublicationRef');
    const versionLabel = assertOptionalTrimmed(input.versionLabel, 'QXRead versionLabel');
    return Object.freeze({
        allowed: true,
        request: Object.freeze({
            publishRequestId,
            authorAccountId: input.authorAccount.authorAccountId,
            accountId: input.authorAccount.accountId,
            publicationId: input.publicationId,
            title,
            qxpPublicationRef,
            versionLabel,
            state: 'draft',
            policyRequiresAdminApproval: input.policyRequiresAdminApproval !== false,
            timeline: appendEvent([], 'created', createdAt, 'Draft PublishRequest created'),
            isQxwDocument: false,
            mutatesQxp: false,
            mutatesQxw: false,
            createdAt,
            updatedAt: createdAt,
        }),
    });
}
/**
 * Submit draft → in_review only when readiness passes. Does not mutate QXP/QXW.
 */
export function submitPublishRequest(input) {
    assertNoForbiddenFoundationKey(input, 'QXRead submit publish request');
    assertT6E2SecurityIntact();
    const access = authorizeAuthorSurfaceAccess({
        session: input.session,
        account: input.account,
        authorAccount: input.authorAccount,
    });
    if (!access.allowed) {
        return Object.freeze({
            allowed: false,
            reason: access.reason,
            request: input.request,
        });
    }
    if (input.request.authorAccountId !== input.authorAccount.authorAccountId) {
        return Object.freeze({
            allowed: false,
            reason: 'Own-resource scope denied — PublishRequest belongs to another AuthorAccount',
            request: input.request,
        });
    }
    if (input.request.state !== 'draft') {
        return Object.freeze({
            allowed: false,
            reason: `PublishRequest submit requires draft state (current: ${input.request.state})`,
            request: input.request,
        });
    }
    const readiness = evaluatePublishReadiness(input.readiness);
    const submittedAt = input.submittedAt ?? new Date().toISOString();
    assertTimestamp(submittedAt, 'QXRead publish submittedAt');
    const withCheck = Object.freeze({
        ...input.request,
        timeline: appendEvent(input.request.timeline, 'readiness_checked', submittedAt, readiness.ready ? 'Readiness passed' : `Missing: ${readiness.missing.join(', ')}`),
        updatedAt: submittedAt,
    });
    if (!readiness.ready) {
        return Object.freeze({
            allowed: false,
            reason: 'PublishRequest submit blocked — readiness checklist incomplete',
            readiness,
            request: withCheck,
        });
    }
    return Object.freeze({
        allowed: true,
        readiness,
        request: Object.freeze({
            ...withCheck,
            state: assertPublishState('in_review', 'QXRead publish state'),
            timeline: appendEvent(withCheck.timeline, 'submitted', submittedAt, 'Submitted for review'),
            updatedAt: submittedAt,
        }),
    });
}
/**
 * Author self-approve is denied when Admin policy requires review (default).
 */
export function attemptAuthorSelfApprove(input) {
    assertNoForbiddenFoundationKey(input, 'QXRead author self-approve');
    assertT6E2SecurityIntact();
    const attemptedAt = input.attemptedAt ?? new Date().toISOString();
    assertTimestamp(attemptedAt, 'QXRead self-approve attemptedAt');
    const access = authorizeAuthorSurfaceAccess({
        session: input.session,
        account: input.account,
        authorAccount: input.authorAccount,
    });
    if (!access.allowed) {
        return Object.freeze({
            allowed: false,
            reason: access.reason,
            request: input.request,
        });
    }
    if (input.request.policyRequiresAdminApproval) {
        return Object.freeze({
            allowed: false,
            reason: 'Author self-approve denied — Admin approval required (T7)',
            request: Object.freeze({
                ...input.request,
                timeline: appendEvent(input.request.timeline, 'self_approve_denied', attemptedAt, 'Author cannot approve when Admin policy requires'),
                updatedAt: attemptedAt,
            }),
        });
    }
    return Object.freeze({
        allowed: false,
        reason: 'Author self-approve denied — publishing approval is not an Author Platform authority',
        request: input.request,
    });
}
/**
 * Record Admin (T7) review outcome onto PublishRequest. Author surfaces do not call as self.
 */
export function applyExternalReviewDecision(input) {
    assertNoForbiddenFoundationKey(input, 'QXRead external review decision');
    assertT6E2SecurityIntact();
    if (input.actor !== 'admin_ops') {
        throw new Error('QXRead review decision requires admin_ops actor');
    }
    if (input.request.state !== 'in_review') {
        throw new Error('QXRead review decision requires in_review state');
    }
    const decidedAt = input.decidedAt ?? new Date().toISOString();
    assertTimestamp(decidedAt, 'QXRead review decidedAt');
    const note = assertOptionalTrimmed(input.note, 'QXRead review note');
    const nextState = input.decision === 'approved' ? 'approved' : 'rejected';
    const kind = input.decision === 'approved' ? 'review_approved' : 'review_rejected';
    return Object.freeze({
        ...input.request,
        state: assertPublishState(nextState, 'QXRead publish state'),
        timeline: appendEvent(input.request.timeline, kind, decidedAt, note ?? `External review ${input.decision}`),
        updatedAt: decidedAt,
    });
}
/**
 * Promote approved → live (catalog release orchestration). Does not edit QXP bytes.
 */
export function releasePublishRequestLive(input) {
    assertNoForbiddenFoundationKey(input, 'QXRead release publish live');
    assertT6E2SecurityIntact();
    const access = authorizeAuthorSurfaceAccess({
        session: input.session,
        account: input.account,
        authorAccount: input.authorAccount,
    });
    if (!access.allowed) {
        return Object.freeze({
            allowed: false,
            reason: access.reason,
            request: input.request,
        });
    }
    if (input.request.authorAccountId !== input.authorAccount.authorAccountId) {
        return Object.freeze({
            allowed: false,
            reason: 'Own-resource scope denied — PublishRequest belongs to another AuthorAccount',
            request: input.request,
        });
    }
    if (input.request.state !== 'approved') {
        return Object.freeze({
            allowed: false,
            reason: `PublishRequest release requires approved state (current: ${input.request.state})`,
            request: input.request,
        });
    }
    if (input.request.qxpPublicationRef === null) {
        return Object.freeze({
            allowed: false,
            reason: 'PublishRequest release requires bound qxpPublicationRef',
            request: input.request,
        });
    }
    const releasedAt = input.releasedAt ?? new Date().toISOString();
    assertTimestamp(releasedAt, 'QXRead publish releasedAt');
    return Object.freeze({
        allowed: true,
        request: Object.freeze({
            ...input.request,
            state: 'live',
            timeline: appendEvent(input.request.timeline, 'released_live', releasedAt, 'Catalog live — QXP truth unchanged by Author Platform'),
            updatedAt: releasedAt,
            mutatesQxp: false,
            mutatesQxw: false,
            isQxwDocument: false,
        }),
    });
}
export function projectPublishTimeline(request) {
    assertT6E2SecurityIntact();
    return request.timeline;
}
//# sourceMappingURL=author-publish-request.js.map